import { z } from 'zod';

import { CountryCodeSchema } from '../../questions/country-code/country-code';
import { GenderSchema } from '../../users/gender';
import { UserStatusSchema } from '../../users/user-status';

/** A decade, which is longer than the platform has existed. */
const MAX_ACTIVITY_DAYS = 3650;

/**
 * Who a campaign reaches — a **whitelisted** filter set (N7), never a raw column
 * passthrough (`03-notifications.md` §Campaigns).
 *
 * ## Why every clause here is paranoid
 *
 * This is the one object on the platform whose mistakes are irreversible and
 * unbounded at the same time: a filter that parses but narrows nothing reaches
 * every member, once, and no correction can recall it. So the schema refuses
 * three things that a laxer contract would wave through — an undeclared key (a
 * filter the repository will never apply), an empty array (which reads as "no
 * filter" once it becomes an `in ()`), and a band that can match nobody.
 *
 * The schema is only half the law. The other half is the api's clause map, whose
 * spec asserts it covers {@link CAMPAIGN_SEGMENT_KEYS} — `users-list-query.ts`
 * records what happens without it: `roleKey` was whitelisted here and absent
 * there, so it validated and was then silently ignored. On a member list that is
 * a confusing screen; here it is the wrong audience.
 *
 * The filter `02-build.md` §P21 calls `entitlement` is {@link CampaignSegment}'s
 * `hasEntitlement`, and it arrived in P23b3d — the last of the five. It is the
 * one clause the notifications module does not build for itself: N14 keeps the
 * live-entitlement predicate inside `modules/billing`, which hands back a
 * correlated `exists` through `ENTITLEMENT_AUDIENCE_PORT`.
 */
const CampaignSegmentShape = {
  genders: z.array(GenderSchema).min(1).optional(),
  statuses: z.array(UserStatusSchema).min(1).optional(),
  /**
   * Matched against the seeded `country` profile answer. It is a *code* list,
   * not content — the clients render the name from `country.<CODE>`.
   */
  countryCodes: z.array(CountryCodeSchema).min(1).optional(),
  /**
   * Whether the member holds a **live** entitlement right now.
   *
   * "Live" is billing's own reading and not this module's: a status of `active`
   * or `grace` **and** a period end that has not passed (an open-ended comp has
   * none). A row still stamped `active` past its end entitles nobody — the
   * expiry sweep simply has not reached it — so a status-only reading would send
   * "thanks for subscribing" to somebody whose access already stopped.
   *
   * `false` is a real audience rather than the filter being unset: it reaches
   * members who never subscribed **and** members who lapsed, which is exactly
   * the win-back campaign. Both answers therefore narrow, and
   * {@link isEveryoneSegment} counts this key like any other.
   *
   * Independent of `billing.planMode`: the mode decides what an entitlement
   * *unlocks*, never whether one is *held*, so this segment means the same thing
   * in a `duration` deployment and a `feature` one.
   */
  hasEntitlement: z.boolean().optional(),
  /** Signed in at some point within the last N days. */
  activeWithinDays: z.number().int().min(1).max(MAX_ACTIVITY_DAYS).optional(),
  /** Has not signed in for at least N days. */
  dormantForDays: z.number().int().min(1).max(MAX_ACTIVITY_DAYS).optional(),
  /**
   * Whether a member who has **never** signed in counts as dormant.
   *
   * `users.last_active_at` is a login stamp and is `null` for such a member, so
   * without this the answer would fall out of the SQL's null handling rather
   * than out of a decision — and a "we miss you" campaign would silently skip
   * the most dormant members there are. Omitted means **yes**: never having
   * arrived is the plain reading of dormant.
   */
  includeNeverActive: z.boolean().optional(),
} as const;

export const CampaignSegmentSchema = z
  .object(CampaignSegmentShape)
  .strict()
  .refine(
    (segment) =>
      segment.activeWithinDays === undefined ||
      segment.dormantForDays === undefined ||
      segment.dormantForDays < segment.activeWithinDays,
    {
      message: 'dormantForDays must be smaller than activeWithinDays',
      path: ['dormantForDays'],
    },
  )
  .refine(
    (segment) => segment.includeNeverActive === undefined || segment.dormantForDays !== undefined,
    {
      message: 'includeNeverActive only applies alongside dormantForDays',
      path: ['includeNeverActive'],
    },
  );

export type CampaignSegment = z.infer<typeof CampaignSegmentSchema>;

export type CampaignSegmentKey = keyof CampaignSegment;

/**
 * Every key the segment accepts, **derived** from the schema.
 *
 * Read by the api's clause map so a filter added here without a clause there
 * fails a test that says why, rather than shipping as a wider audience.
 */
export const CAMPAIGN_SEGMENT_KEYS: readonly CampaignSegmentKey[] = Object.keys(
  CampaignSegmentShape,
) as readonly CampaignSegmentKey[];

/** Keys that qualify another filter rather than narrowing the population. */
const QUALIFYING_KEYS: readonly CampaignSegmentKey[] = ['includeNeverActive'];

/**
 * Whether this segment narrows nothing — i.e. reaches every member.
 *
 * A legitimate audience (the legal re-consent notice is exactly it), and the one
 * the composer must say out loud before anybody presses send.
 */
export function isEveryoneSegment(segment: CampaignSegment): boolean {
  return CAMPAIGN_SEGMENT_KEYS.filter((key) => !QUALIFYING_KEYS.includes(key)).every(
    (key) => segment[key] === undefined,
  );
}
